import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Truck,
  Upload,
  Calculator,
  Trash2,
  Loader2,
  RefreshCw,
  TrendingUp,
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fmt = (n) =>
  (Number(n) || 0).toLocaleString("en-ZA", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

export default function CourierCostsTab() {
  const [stats, setStats] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);

  // Upload + preview state
  const [uploadOpen, setUploadOpen] = useState(false);
  const [parseLoading, setParseLoading] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [parsed, setParsed] = useState(null);
  const [parsedRows, setParsedRows] = useState([]);
  // Track the file the user picked so we can show an explicit "Upload & Parse"
  // button (the modal used to auto-parse on file-pick which left the user with
  // only a Cancel button when the parse failed silently).
  const [selectedFile, setSelectedFile] = useState(null);
  // Ref to the file input so the button can ALSO read the file directly from
  // the DOM — belt-and-braces in case onChange wasn't captured for any reason.
  const fileInputRef = useRef(null);

  // Estimator state
  const [estSender, setEstSender] = useState("");
  const [estOrigin, setEstOrigin] = useState("");
  const [estWeight, setEstWeight] = useState("5");
  const [estimate, setEstimate] = useState(null);
  const [estLoading, setEstLoading] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [s, inv] = await Promise.all([
        axios.get(`${API}/courier/stats`),
        axios.get(`${API}/courier/invoices`),
      ]);
      setStats(s.data);
      setInvoices(inv.data?.invoices || []);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load courier stats");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  // ---------------- Upload + Parse ----------------
  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Please upload a PDF invoice");
      e.target.value = "";
      return;
    }
    // Reset any prior parse state and stash the file — the user must click
    // "Upload & Parse" to actually send it.
    setSelectedFile(file);
    setParsed(null);
    setParsedRows([]);
  };

  const parseSelectedFile = async () => {
    // Read the file straight from the DOM input first — this is the source of
    // truth and immune to any state-update race the user has reported. We fall
    // back to the React state copy if for some reason the ref isn't attached.
    const fileFromInput =
      (fileInputRef.current && fileInputRef.current.files && fileInputRef.current.files[0]) ||
      null;
    const file = fileFromInput || selectedFile;
    if (!file) {
      toast.error("Choose a PDF file first.");
      return;
    }
    if (!file.name.toLowerCase().endsWith(".pdf")) {
      toast.error("That doesn't look like a PDF — please choose a .pdf file.");
      return;
    }
    // Make sure state is in sync (so the helper line + disabled flag reflect
    // reality if the click is what populates it)
    if (file !== selectedFile) setSelectedFile(file);

    setParseLoading(true);
    setParsed(null);
    setParsedRows([]);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/courier/invoices/parse`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setParsed(res.data);
      const rows = (res.data.waybills || []).map((w) => ({
        ...w,
        _skip: !!w.already_imported,
      }));
      setParsedRows(rows);
      if (rows.length === 0) {
        toast.warning("No waybill rows could be parsed from this PDF");
      } else {
        toast.success(`Parsed ${rows.length} waybills — review and confirm below.`);
      }
    } catch (err) {
      console.error("[courier parse error]", err);
      const detail =
        err?.response?.data?.detail ||
        err?.message ||
        "Could not parse PDF — see browser console for details.";
      toast.error(detail);
    } finally {
      setParseLoading(false);
    }
  };

  const updateRow = (idx, key, value) => {
    setParsedRows((prev) =>
      prev.map((r, i) => (i === idx ? { ...r, [key]: value } : r))
    );
  };

  const importParsed = async () => {
    if (!parsed) return;
    const keep = parsedRows.filter((r) => !r._skip);
    if (keep.length === 0) {
      toast.error("No waybills selected to import");
      return;
    }
    setImportLoading(true);
    try {
      const payload = {
        courier: "EPX",
        invoice_number: parsed.header?.invoice_number,
        invoice_date: parsed.header?.invoice_date,
        invoice_period_from: parsed.header?.invoice_period_from,
        invoice_period_until: parsed.header?.invoice_period_until,
        waybills: keep.map((r) => ({
          waybill_number: r.waybill_number,
          waybill_date: r.waybill_date,
          sender_name: r.sender_name,
          recipient_name: r.recipient_name,
          origin_code: r.origin_code,
          origin_city: r.origin_city || r.origin_code,
          destination_code: r.destination_code,
          destination_city: r.destination_city,
          service_code: r.service_code,
          pieces: Number(r.pieces) || 0,
          actual_mass_kg: Number(r.actual_mass_kg) || 0,
          charge_mass_kg: Number(r.charge_mass_kg) || 0,
          basic_charge: Number(r.basic_charge) || 0,
          outly: Number(r.outly) || 0,
          liability: Number(r.liability) || 0,
          fuel: Number(r.fuel) || 0,
          other: Number(r.other) || 0,
          sub_total: Number(r.sub_total) || 0,
          vat: Number(r.vat) || 0,
          total: Number(r.total) || 0,
        })),
      };
      const res = await axios.post(`${API}/courier/invoices/import`, payload);
      toast.success(
        `Imported ${res.data.imported} waybills` +
          (res.data.skipped_duplicates ? ` (skipped ${res.data.skipped_duplicates} duplicates)` : "")
      );
      setUploadOpen(false);
      setParsed(null);
      setParsedRows([]);
      setSelectedFile(null);
      fetchAll();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || "Failed to import");
    } finally {
      setImportLoading(false);
    }
  };

  const deleteInvoice = async (invoice_number) => {
    if (!window.confirm(`Delete all waybills for invoice ${invoice_number}? This cannot be undone.`)) return;
    try {
      const res = await axios.delete(
        `${API}/courier/invoices/by-number/${encodeURIComponent(invoice_number)}`
      );
      toast.success(`Deleted ${res.data.deleted} waybills`);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };

  // ---------------- Estimator ----------------
  const runEstimate = async () => {
    setEstLoading(true);
    try {
      const params = new URLSearchParams();
      if (estSender.trim()) params.set("sender", estSender.trim());
      if (estOrigin.trim()) params.set("origin", estOrigin.trim());
      params.set("weight_kg", String(Number(estWeight) || 1));
      const res = await axios.get(`${API}/courier/estimate?${params.toString()}`);
      setEstimate(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Estimate failed");
    } finally {
      setEstLoading(false);
    }
  };

  const totals = stats?.totals;

  return (
    <div className="space-y-4" data-testid="courier-costs-tab">
      {/* Header + actions */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Truck className="h-6 w-6 text-amber-700" />
          <div>
            <h2 className="text-xl font-bold">Courier Costs (EPX)</h2>
            <p className="text-sm text-gray-500">
              Upload your monthly EPX tax invoice. Build up an approximate cost-per-supplier and cost-per-origin baseline.
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchAll} data-testid="courier-refresh-btn">
            <RefreshCw className={`h-4 w-4 mr-1 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
          <Button
            onClick={() => setUploadOpen(true)}
            className="bg-amber-600 hover:bg-amber-700"
            data-testid="courier-upload-btn"
          >
            <Upload className="h-4 w-4 mr-1" />
            Upload EPX Invoice
          </Button>
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <Card>
          <CardContent className="pt-4">
            <p className="text-xs uppercase text-gray-500">Total spent (incl VAT)</p>
            <p className="text-2xl font-bold text-amber-700" data-testid="kpi-total-spent">
              R {fmt(totals?.total)}
            </p>
            <p className="text-xs text-gray-400 mt-1">
              Excl VAT: R {fmt(totals?.sub_total)}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <p className="text-xs uppercase text-gray-500">Shipments</p>
            <p className="text-2xl font-bold" data-testid="kpi-shipments">
              {totals?.shipments || 0}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <p className="text-xs uppercase text-gray-500">Total kg charged</p>
            <p className="text-2xl font-bold" data-testid="kpi-kg">
              {fmt(totals?.kg_charged)}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <p className="text-xs uppercase text-gray-500">Avg per shipment</p>
            <p className="text-2xl font-bold">
              R {fmt(totals?.shipments ? totals.total / totals.shipments : 0)}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Estimator */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Calculator className="h-5 w-5 text-amber-700" />
            Quick Estimator
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div>
              <Label>Sender (supplier name)</Label>
              <Input
                value={estSender}
                onChange={(e) => setEstSender(e.target.value)}
                placeholder="e.g. NETAFIRM, HYDRUS…"
                data-testid="est-sender-input"
              />
            </div>
            <div>
              <Label>Or Origin (city / 3-letter code)</Label>
              <Input
                value={estOrigin}
                onChange={(e) => setEstOrigin(e.target.value)}
                placeholder="e.g. DEN, POL, KEMPTONPARK"
                data-testid="est-origin-input"
              />
            </div>
            <div>
              <Label>Estimated weight (kg)</Label>
              <Input
                type="number"
                step="0.1"
                min="0.1"
                value={estWeight}
                onChange={(e) => setEstWeight(e.target.value)}
                data-testid="est-weight-input"
              />
            </div>
            <div className="flex items-end">
              <Button
                onClick={runEstimate}
                disabled={estLoading}
                className="w-full bg-amber-600 hover:bg-amber-700"
                data-testid="est-run-btn"
              >
                {estLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Estimate"}
              </Button>
            </div>
          </div>
          {estimate && (
            <div className="rounded-md border border-amber-200 bg-amber-50 p-4" data-testid="est-result">
              {estimate.estimate == null ? (
                <p className="text-sm text-amber-800">{estimate.message || "No data yet."}</p>
              ) : (
                <>
                  <div className="flex flex-wrap gap-x-6 gap-y-2 items-baseline">
                    <span className="text-xs uppercase text-amber-800">Estimated cost</span>
                    <span className="text-3xl font-bold text-amber-900">
                      ≈ R {fmt(estimate.estimate)}
                    </span>
                    <span className="text-xs text-amber-700">
                      ({estimate.matched_on}, {estimate.sample_size} past shipments)
                    </span>
                  </div>
                  <p className="text-xs text-gray-700 mt-2">
                    Avg R {fmt(estimate.avg_per_kg)}/kg · Avg per shipment R {fmt(estimate.avg_per_shipment)} · Includes basic R{fmt(estimate.breakdown_avg?.basic)} + fuel R{fmt(estimate.breakdown_avg?.fuel)} + outly R{fmt(estimate.breakdown_avg?.outly)} on average.
                  </p>
                  <p className="text-xs text-gray-500 italic mt-1">{estimate.note}</p>
                </>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Stats: by sender and by origin side by side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-amber-700" />
              By Supplier (sender)
            </CardTitle>
          </CardHeader>
          <CardContent>
            {(stats?.by_sender || []).length === 0 ? (
              <p className="text-sm text-gray-500 italic py-4 text-center">No data yet — upload an invoice.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Sender</TableHead>
                    <TableHead className="text-right">Ship.</TableHead>
                    <TableHead className="text-right">kg</TableHead>
                    <TableHead className="text-right">Avg R/kg</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {stats.by_sender.slice(0, 15).map((s) => (
                    <TableRow
                      key={s.sender_normalized}
                      className="hover:bg-amber-50 cursor-pointer"
                      onClick={() => {
                        setEstSender(s.sender_name || s.sender_normalized);
                        setEstOrigin("");
                      }}
                      data-testid={`courier-sender-row-${s.sender_normalized}`}
                    >
                      <TableCell className="font-medium">{s.sender_name || s.sender_normalized}</TableCell>
                      <TableCell className="text-right">{s.shipments}</TableCell>
                      <TableCell className="text-right">{fmt(s.kg_charged)}</TableCell>
                      <TableCell className="text-right">R {fmt(s.avg_per_kg)}</TableCell>
                      <TableCell className="text-right font-semibold">R {fmt(s.total_spent)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-amber-700" />
              By Origin
            </CardTitle>
          </CardHeader>
          <CardContent>
            {(stats?.by_origin || []).length === 0 ? (
              <p className="text-sm text-gray-500 italic py-4 text-center">No data yet — upload an invoice.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Origin</TableHead>
                    <TableHead className="text-right">Ship.</TableHead>
                    <TableHead className="text-right">kg</TableHead>
                    <TableHead className="text-right">Avg R/kg</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {stats.by_origin.map((o) => (
                    <TableRow
                      key={`${o.origin_code}-${o.origin_city}`}
                      className="hover:bg-amber-50 cursor-pointer"
                      onClick={() => {
                        setEstOrigin(o.origin_city || o.origin_code);
                        setEstSender("");
                      }}
                      data-testid={`courier-origin-row-${o.origin_code}`}
                    >
                      <TableCell className="font-medium">
                        {o.origin_city || o.origin_code}
                        <span className="text-xs text-gray-400 ml-2">{o.origin_code}</span>
                      </TableCell>
                      <TableCell className="text-right">{o.shipments}</TableCell>
                      <TableCell className="text-right">{fmt(o.kg_charged)}</TableCell>
                      <TableCell className="text-right">R {fmt(o.avg_per_kg)}</TableCell>
                      <TableCell className="text-right font-semibold">R {fmt(o.total_spent)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Imported invoices list */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg">Uploaded EPX Invoices</CardTitle>
        </CardHeader>
        <CardContent>
          {invoices.length === 0 ? (
            <p className="text-sm text-gray-500 italic py-4 text-center">No invoices yet.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Invoice #</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Period</TableHead>
                  <TableHead className="text-right">Waybills</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {invoices.map((inv) => (
                  <TableRow key={inv.invoice_number}>
                    <TableCell className="font-mono">{inv.invoice_number}</TableCell>
                    <TableCell>{inv.invoice_date || "—"}</TableCell>
                    <TableCell className="text-sm text-gray-500">
                      {inv.period_from || "?"} → {inv.period_until || "?"}
                    </TableCell>
                    <TableCell className="text-right">{inv.waybills}</TableCell>
                    <TableCell className="text-right font-semibold">R {fmt(inv.total)}</TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-red-600 hover:bg-red-50"
                        onClick={() => deleteInvoice(inv.invoice_number)}
                        data-testid={`courier-invoice-delete-${inv.invoice_number}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Upload + preview dialog */}
      <Dialog open={uploadOpen} onOpenChange={(o) => { setUploadOpen(o); if (!o) { setParsed(null); setParsedRows([]); setSelectedFile(null); } }}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto" data-testid="courier-upload-dialog">
          <DialogHeader>
            <DialogTitle>Upload EPX Tax Invoice (PDF)</DialogTitle>
          </DialogHeader>

          {!parsed && (
            <div className="space-y-3 py-4">
              <Label>Choose your monthly EPX invoice PDF:</Label>
              <Input
                ref={fileInputRef}
                type="file"
                accept="application/pdf,.pdf"
                onChange={handleFileChange}
                disabled={parseLoading}
                data-testid="courier-file-input"
              />
              {selectedFile && (
                <div className="text-xs text-gray-600 flex items-center gap-2">
                  Selected: <span className="font-mono">{selectedFile.name}</span>
                  <span className="text-gray-400">({Math.round(selectedFile.size / 1024)} KB)</span>
                </div>
              )}
              <p className="text-xs text-gray-500">
                We&apos;ll extract waybill rows (sender, origin, destination, weight, total) so you can review and confirm before saving.
              </p>
              {parseLoading && (
                <div className="flex items-center gap-2 text-sm text-amber-700">
                  <Loader2 className="h-4 w-4 animate-spin" /> Parsing PDF…
                </div>
              )}
              <div className="flex justify-end pt-2">
                <Button
                  onClick={parseSelectedFile}
                  disabled={parseLoading}
                  className="bg-amber-600 hover:bg-amber-700 disabled:opacity-60"
                  data-testid="courier-upload-parse-btn"
                >
                  {parseLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-1 animate-spin" /> Parsing…
                    </>
                  ) : (
                    <>Upload &amp; Parse</>
                  )}
                </Button>
              </div>
            </div>
          )}

          {parsed && (
            <div className="space-y-3 py-2">
              <div className="bg-amber-50 border border-amber-200 rounded p-3 text-sm">
                <strong>Invoice {parsed.header?.invoice_number || "(none)"}</strong>{" "}
                · {parsed.header?.invoice_date || "—"} · period{" "}
                {parsed.header?.invoice_period_from || "?"} → {parsed.header?.invoice_period_until || "?"}
                <br />
                Parsed <strong>{parsed.totals?.count}</strong> waybills · sub-total R{fmt(parsed.totals?.sub_total)} · VAT R{fmt(parsed.totals?.vat)} · total <strong>R {fmt(parsed.totals?.total)}</strong>
              </div>

              <div className="overflow-x-auto border rounded">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50 text-xs">
                      <TableHead className="w-12">Skip</TableHead>
                      <TableHead>Waybill</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Sender</TableHead>
                      <TableHead>Origin</TableHead>
                      <TableHead>Dest</TableHead>
                      <TableHead className="text-right">Mass kg</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {parsedRows.map((r, idx) => (
                      <TableRow
                        key={r.waybill_number + idx}
                        className={`text-sm ${r._skip ? "opacity-50" : ""} ${r.already_imported ? "bg-yellow-50" : ""}`}
                      >
                        <TableCell>
                          <input
                            type="checkbox"
                            checked={!!r._skip}
                            onChange={(e) => updateRow(idx, "_skip", e.target.checked)}
                            data-testid={`parsed-row-skip-${idx}`}
                          />
                          {r.already_imported && (
                            <span className="block text-xs text-yellow-700 mt-1">dup</span>
                          )}
                        </TableCell>
                        <TableCell className="font-mono text-xs">{r.waybill_number}</TableCell>
                        <TableCell className="text-xs">{r.waybill_date || "—"}</TableCell>
                        <TableCell>
                          <Input
                            className="h-7 text-xs"
                            value={r.sender_name || ""}
                            onChange={(e) => updateRow(idx, "sender_name", e.target.value)}
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            className="h-7 text-xs w-24"
                            value={r.origin_code || ""}
                            onChange={(e) => updateRow(idx, "origin_code", e.target.value)}
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            className="h-7 text-xs"
                            value={r.destination_city || ""}
                            onChange={(e) => updateRow(idx, "destination_city", e.target.value)}
                          />
                        </TableCell>
                        <TableCell className="text-right">
                          <Input
                            className="h-7 text-xs w-20 text-right"
                            type="number"
                            step="0.1"
                            value={r.charge_mass_kg}
                            onChange={(e) => updateRow(idx, "charge_mass_kg", e.target.value)}
                          />
                        </TableCell>
                        <TableCell className="text-right">
                          <Input
                            className="h-7 text-xs w-24 text-right"
                            type="number"
                            step="0.01"
                            value={r.total}
                            onChange={(e) => updateRow(idx, "total", e.target.value)}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setUploadOpen(false)}>
              Cancel
            </Button>
            {parsed && (
              <Button
                onClick={importParsed}
                disabled={importLoading}
                className="bg-amber-600 hover:bg-amber-700"
                data-testid="courier-import-btn"
              >
                {importLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-1" />
                ) : (
                  <Upload className="h-4 w-4 mr-1" />
                )}
                Save Selected Waybills
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
